import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getMobileUploadInfo, sendMobileUpload } from '../services/api';

/**
 * The page the phone lands on after scanning the QR code.
 *
 * Nobody signs in here: the token in the URL is the whole authorisation. One
 * screen, one action, the button under the thumb — someone is holding this at
 * arm's length in front of a damaged wall.
 *
 * Photos go up one request at a time rather than as one batch: the upload cap
 * applies per request, so a dozen shots from a modern phone would otherwise be
 * refused wholesale, and a single failure would take the good ones down with it.
 */
export default function MobileUploadPage() {
  const { t } = useTranslation();
  const { token } = useParams();

  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [sent, setSent] = useState([]);

  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    getMobileUploadInfo(token)
      .then((data) => { if (!cancelled) setInfo(data); })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err?.response?.status === 401 ? t('upload.linkExpiredLong') : t('upload.linkUnavailable')
        );
      });
    return () => { cancelled = true; };
  }, [token, t]);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setError(null);

    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      setProgress({ done: i, total: files.length, percent: 0 });
      try {
        const result = await sendMobileUpload(token, [file], (e) => {
          if (e.total) {
            setProgress({
              done: i,
              total: files.length,
              percent: Math.round((e.loaded / e.total) * 100),
            });
          }
        });
        const names = result?.file_names?.length ? result.file_names : [file.name];
        setSent((prev) => [...names, ...prev]);
      } catch (err) {
        setError(
          err?.response?.status === 401
            ? t('upload.linkExpiredLong')
            : t('upload.sendFailed')
        );
        break;
      }
    }
    setProgress(null);
  };

  const pick = (ref) => (e) => {
    upload(e.target.files);
    if (ref.current) ref.current.value = '';
  };

  return (
    <div className="mobile-upload">
      <div className="mobile-upload-card">
        <h1 className="mobile-upload-title">{t('upload.mobileTitle')}</h1>

        {loadError ? (
          <p className="mobile-upload-error">{loadError}</p>
        ) : !info ? (
          <p className="qr-muted">{t('auth.loading')}</p>
        ) : (
          <>
            <p className="mobile-upload-target">
              {info.step_title
                ? t('upload.targetStep', { step: info.step_title })
                : t('upload.targetReport', { report: info.report_title })}
            </p>

            {error && <p className="mobile-upload-error">{error}</p>}

            {progress && (
              <div className="mobile-upload-progress">
                <div className="mobile-upload-bar">
                  <span style={{ width: `${progress.percent}%` }} />
                </div>
                <small>
                  {t('upload.sendingCount', {
                    current: progress.done + 1,
                    total: progress.total,
                  })}
                </small>
              </div>
            )}

            <button
              className="btn btn-primary mobile-upload-action"
              disabled={!!progress}
              onClick={() => cameraRef.current?.click()}
            >
              {sent.length ? t('upload.takeAnother') : t('upload.takePhoto')}
            </button>

            <button
              className="btn btn-secondary mobile-upload-action"
              disabled={!!progress}
              onClick={() => galleryRef.current?.click()}
            >
              {t('upload.chooseFromGallery')}
            </button>

            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={pick(cameraRef)}
            />
            <input
              ref={galleryRef}
              type="file"
              accept="image/*,.heic,.heif"
              multiple
              style={{ display: 'none' }}
              onChange={pick(galleryRef)}
            />

            {sent.length > 0 && (
              <div className="mobile-upload-sent">
                <p>{t('upload.photosSent', { n: sent.length })}</p>
                <ul>
                  {sent.map((name, idx) => (
                    <li key={`${name}-${idx}`}>✓ {name}</li>
                  ))}
                </ul>
                <p className="qr-muted">{t('upload.keepGoing')}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
